import React, { useEffect, useId } from "react";
import * as S from "./styled";

interface AppConfirmationPromptProps {
  show: boolean;
  title: string;
  body: React.ReactNode;
  confirmText: string;
  cancelText?: string;
  onClose: () => void;
  onConfirm: () => void;
  onCancel?: () => void;
}

const AppConfirmationPrompt: React.FC<AppConfirmationPromptProps> = ({
  show,
  title,
  body,
  confirmText,
  cancelText = "Cancel",
  onClose,
  onConfirm,
  onCancel,
}) => {
  const reactId = useId();
  const titleId = `app-confirmation-title-${reactId}`;
  const bodyId = `app-confirmation-description-${reactId}`;

  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, show]);

  if (!show) return null;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }

    onClose();
  };

  return (
    <S.Backdrop onClick={onClose}>
      <S.Dialog
        aria-describedby={bodyId}
        aria-labelledby={titleId}
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <S.Title id={titleId}>{title}</S.Title>
        <S.Body id={bodyId}>{body}</S.Body>
        <S.Actions>
          <S.SecondaryButton type="button" onClick={handleCancel}>
            {cancelText}
          </S.SecondaryButton>
          <S.PrimaryButton type="button" onClick={onConfirm}>
            {confirmText}
          </S.PrimaryButton>
        </S.Actions>
      </S.Dialog>
    </S.Backdrop>
  );
};

export default AppConfirmationPrompt;
